import { isString } from 'lodash';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
// material
import { alpha, experimentalStyled as styled } from '@material-ui/core/styles';
import { Paper, Box, Typography, Tooltip } from '@material-ui/core';
import { GetAppOutlined } from '@material-ui/icons';
import { useSnackbar } from 'notistack';
// utils
import { fData } from '../../utils/formatNumber';
import {
  downloadImage,
  getAttachmentDownloadUrl,
  getAttachmentDisplayName,
  getFileExtension,
  isImageFile,
  isOfficeFile,
  isPdfFile,
} from 'src/Helpers/FileDownloadUtils';
import { getUploadValidationError } from '../../utils/fileUploadValidation';
//
import { UploadIllustration } from '../../assets';
import { MIconButton } from '../@material-extend';
import { Description } from '@material-ui/icons';

// ----------------------------------------------------------------------

const DropZoneStyle = styled('div')(({ theme }) => ({
  outline: 'none',
  display: 'flex',
  overflow: 'hidden',
  textAlign: 'center',
  position: 'relative',
  alignItems: 'center',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: theme.spacing(5, 0),
  borderRadius: theme.shape.borderRadius,
  transition: theme.transitions.create('padding'),
  backgroundColor: theme.palette.background.neutral,
  border: `1px dashed ${theme.palette.grey[500_32]}`,
  '&:hover': {
    opacity: 0.72,
    cursor: 'pointer'
  },
  [theme.breakpoints.up('md')]: { textAlign: 'left', flexDirection: 'row' }
}));

// ----------------------------------------------------------------------

UploadSingleFile.propTypes = {
  error: PropTypes.bool,
  file: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  sx: PropTypes.object,
  allowDownload: PropTypes.bool,
  caption: PropTypes.node,
};

export default function UploadSingleFile({
  error,
  file,
  fullImage,
  sx,
  allowDownload = false,
  caption,
  ...other
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { getRootProps, getInputProps, isDragActive, isDragReject, fileRejections } = useDropzone({
    multiple: false,
    ...other,
    validator: (selectedFile) =>
      getUploadValidationError(selectedFile) ||
      (typeof other.validator === 'function' ? other.validator(selectedFile) : null)
  });

  let canDownload = false;
  if (allowDownload && file) {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const isClient = user?.isClient || user?.isclient;
      const userType = isClient ? 3 : user?.user_type;
      canDownload = !isClient && userType !== 3;
    } catch {
      canDownload = false;
    }
  }

  const resolveFileUrl = () => {
    if (!file) return '';
    if (isString(file)) return file;
    return getAttachmentDownloadUrl(file) || file.preview || '';
  };

  const handleDownload = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const fileUrl = resolveFileUrl();
    if (!fileUrl) {
      enqueueSnackbar('No file to download', { variant: 'warning' });
      return;
    }
    const displayName = isString(file) ? null : getAttachmentDisplayName(file);
    downloadImage(
      fileUrl,
      displayName,
      0,
      () => enqueueSnackbar('File downloaded successfully', { variant: 'success' }),
      () => enqueueSnackbar('Failed to download file', { variant: 'error' })
    );
  };

  const ShowRejectionItems = () => (
    <Paper
      variant="outlined"
      sx={{
        py: 1,
        px: 2,
        mt: 3,
        borderColor: 'error.light',
        bgcolor: (theme) => alpha(theme.palette.error.main, 0.08)
      }}
    >
      {fileRejections.map(({ file, errors }) => {
        const { path, size } = file;
        return (
          <Box key={path} sx={{ my: 1 }}>
            <Typography variant="subtitle2" noWrap>
              {path} - {fData(size)}
            </Typography>
            {errors.map((e) => (
              <Typography key={e.code} variant="caption" component="p">
                - {e.message}
              </Typography>
            ))}
          </Box>
        );
      })}
    </Paper>
  );

  return (
    <Box sx={{ width: '100%', ...sx }}>
      <DropZoneStyle
        {...getRootProps()}
        sx={{
          ...(isDragActive && { opacity: 0.72 }),
          ...((isDragReject || error) && {
            color: 'error.main',
            borderColor: 'error.light',
            bgcolor: 'error.lighter'
          }),
          ...(file && {
            padding: theme => theme.spacing(2),
            minHeight: 'auto',
            cursor: 'pointer',
          })
        }}
      >
        <input {...getInputProps()} />

        {!file && <UploadIllustration sx={{ width: 220 }} />}

        {!file && (
          <Box sx={{ p: 3, ml: { md: 2 } }}>
            <Typography gutterBottom variant="h5">
              Drop or Select file
            </Typography>

            <Typography variant="body2" >
              Drop files here or click&nbsp;
              <Typography variant="body2" component="span" sx={{ color: 'primary.main' }}>
                browse
              </Typography>
              &nbsp;thorough your machine
            </Typography>
          </Box>
        )}

        {file && (
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              borderRadius: 1,
              overflow: 'hidden',
              display: 'inline-block',
            }}
          >
            {(() => {
              const fileUrl = resolveFileUrl();
              const fileType = isString(file) ? '' : file?.file_type || file?.type || '';
              const ext = getFileExtension(fileUrl) || (isString(file) ? '' : file?.extension || '');
              const canShowImage =
                isImageFile(fileType) ||
                ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(String(ext).toLowerCase());
              const label =
                (isString(file) ? null : getAttachmentDisplayName(file)) ||
                ext ||
                'File';

              if (canShowImage) {
                return (
                  <Box
                    component="img"
                    alt="file preview"
                    src={isString(file) ? file : file.preview || fileUrl}
                    sx={{
                      width: '100%',
                      maxHeight: fullImage ? 320 : 200,
                      objectFit: fullImage ? 'contain' : 'cover',
                      display: 'block',
                      borderRadius: 1,
                    }}
                  />
                );
              }

              // Avoid <img>/<iframe> of xlsx/pdf binaries here — browser downloads instead.
              return (
                <Box
                  sx={{
                    width: '100%',
                    minHeight: fullImage ? 320 : 160,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    bgcolor: 'background.neutral',
                    borderRadius: 1,
                  }}
                >
                  <Description style={{ fontSize: 48, color: 'gray' }} />
                  <Typography variant="caption" noWrap sx={{ maxWidth: '90%', px: 1 }}>
                    {isPdfFile(fileUrl, fileType)
                      ? 'PDF'
                      : isOfficeFile(fileUrl, fileType)
                      ? String(ext || 'Office').toUpperCase()
                      : label}
                  </Typography>
                </Box>
              );
            })()}
            {canDownload && (
              <Box sx={{ top: 8, left: 8, position: 'absolute', zIndex: 1 }}>
                <Tooltip title="Download">
                  <MIconButton
                    size="small"
                    onClick={handleDownload}
                    sx={{
                      p: '2px',
                      color: 'common.white',
                      bgcolor: (theme) => alpha(theme.palette.grey[900], 0.72),
                      '&:hover': {
                        bgcolor: (theme) => alpha(theme.palette.grey[900], 0.48),
                      },
                    }}
                  >
                    <GetAppOutlined fontSize="small" />
                  </MIconButton>
                </Tooltip>
              </Box>
            )}
          </Box>
        )}
      </DropZoneStyle>

      {caption}
      {fileRejections.length > 0 && <ShowRejectionItems />}
    </Box>
  );
}
